import React, { useMemo, useRef } from "react";
import NoData from "../common/NoData";
import RemoveStagePlayDayMutation from "./mutation/RemoveStagePlayDay";
import AddStagePlayDayModal from "./AddStagePlayDayModal";
import { GlobalStyles } from "../../constants/styles";
import { confirmDelete } from "../common/confirmDelete";
import useThemeColors from "../organization/portal/useThemeColors";
import { alphaFromHex } from "../../util/colors";
import useTranslation from "../../i18n/useTranslation";

export default function StagePlayDaysList({ stageId, playDates, setPlayDates }) {
    const [removeStagePlayDay] = RemoveStagePlayDayMutation();
    const modalElem = useRef(null);
    const colors = useThemeColors();
    const { t } = useTranslation();

    const DAYS = useMemo(
        () => [
            t("stagePlayDaysList.days.monday"),
            t("stagePlayDaysList.days.tuesday"),
            t("stagePlayDaysList.days.wednesday"),
            t("stagePlayDaysList.days.thursday"),
            t("stagePlayDaysList.days.friday"),
            t("stagePlayDaysList.days.saturday"),
            t("stagePlayDaysList.days.sunday"),
        ],
        [t]
    );

    const ui = useMemo(() => {
        return {
            wrap: {
                borderRadius: 18,
                overflow: "hidden",
                border: "1px solid rgba(0,0,0,0.08)",
                background: "#fff",
                boxShadow: "0 18px 44px rgba(0,0,0,0.10)",
            },
            header: {
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                padding: "12px 14px",
                background: `linear-gradient(
                    135deg,
                    ${alphaFromHex(colors.primary, 0.50)},
                    ${alphaFromHex(colors.primary, 0.00)}
                )`,
                borderBottom: "1px solid rgba(0,0,0,0.06)",
            },
            titleWrap: {
                display: "flex",
                alignItems: "center",
                gap: 10,
                minWidth: 0,
            },
            badge: {
                fontSize: 12,
                fontWeight: 900,
                letterSpacing: "0.03em",
                padding: "6px 10px",
                borderRadius: 999,
                background: colors.primary,
                border: "1px solid rgba(0,0,0,0.10)",
                color: colors.textOnPrimary,
                whiteSpace: "nowrap",
            },
            title: {
                margin: 0,
                fontSize: 14,
                fontWeight: 900,
                color: colors.textOnPrimary,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
            },
            sub: {
                margin: 0,
                fontSize: 12.5,
                fontWeight: 700,
                color: colors.textOnPrimary,
            },
            headerRight: {
                display: "flex",
                alignItems: "center",
                gap: 10,
            },
            btn: {
                borderRadius: 12,
                padding: "10px 12px",
                fontWeight: 900,
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                border: "1px solid rgba(0,0,0,0.12)",
                background: "#fff",
                color: "#14151a",
                cursor: "pointer",
            },
            btnPrimary: {
                background: GlobalStyles.colors.canary,
                border: "1px solid rgba(0,0,0,0.10)",
                color: GlobalStyles.colors.woodsmoke,
            },
            body: { padding: 14 },
            sectionCard: {
                borderRadius: 14,
                padding: 12,
                border: "1px solid rgba(0,0,0,0.06)",
                background: "rgba(0,0,0,0.015)",
                marginBottom: 12,
            },
            chip: {
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "6px 10px",
                borderRadius: 999,
                border: "1px solid rgba(0,0,0,0.10)",
                background: colors.secondary,
                color: colors.textOnSecondary,
                fontWeight: 900,
                fontSize: 12,
            },
            actionBtn: {
                width: 38,
                height: 38,
                borderRadius: 12,
                border: "1px solid rgba(0,0,0,0.10)",
                background: "#fff",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
            },
            actionBtnDanger: {
                border: "1px solid rgba(220,53,69,0.35)",
                background: "rgba(220,53,69,0.06)",
                color: "#dc3545",
            },
            muted: {
                color: "rgba(20,21,26,0.60)",
                fontWeight: 700,
                fontSize: 12.5,
            },
        };
    }, [colors.primary, colors.secondary, colors.textOnPrimary, colors.textOnSecondary]);

    const deletePlayDate = async (dateId) => {
        const numericId = parseInt(dateId, 10);

        const res = await removeStagePlayDay({
            variables: { stagePlayDayId: numericId },
        });

        if (res.data?.removeStagePlayDay) {
            setPlayDates((prev) =>
                (prev || []).filter((item) => parseInt(item.id, 10) !== numericId)
            );
        }
    };

    const requestDeletePlayDay = (date) => {
        const dayName =
            DAYS[date.day] || t("stagePlayDaysList.days.selectedDay");

        confirmDelete({
            title: t("stagePlayDaysList.confirm.deleteTitle"),
            message: t("stagePlayDaysList.confirm.deleteMessage", {
                dayName,
                fromTime: date.fromTime,
            }),
            confirmLabel: t("stagePlayDaysList.confirm.delete"),
            cancelLabel: t("stagePlayDaysList.confirm.cancel"),
            danger: true,
            onConfirm: async () => {
                await deletePlayDate(date.id);
            },
        });
    };

    return (
        <>
            <div id="kbola-playdays" style={ui.wrap}>
                <div style={ui.header}>
                    <div style={ui.titleWrap}>
                        <span style={ui.badge}>{playDates?.length || 0}</span>

                        <div style={{ minWidth: 0 }}>
                            <h3 style={ui.title}>
                                {t("stagePlayDaysList.header.title")}
                            </h3>
                            <p style={ui.sub}>
                                {t("stagePlayDaysList.header.subtitle")}
                            </p>
                        </div>
                    </div>

                    <div style={ui.headerRight}>
                        <button
                            type="button"
                            style={{ ...ui.btn, ...ui.btnPrimary }}
                            ref={modalElem}
                            data-toggle="modal"
                            data-target="#add-playday-modal"
                            title={t("stagePlayDaysList.header.addDayTitle")}
                        >
                            <i className="fas fa-plus" />
                            {t("stagePlayDaysList.header.addDay")}
                        </button>
                    </div>
                </div>

                <div style={ui.body}>
                    {playDates?.length ? (
                        <div
                            className="table-responsive"
                            style={{ borderRadius: 14, overflow: "hidden" }}
                        >
                            <table className="table mb-0 kbola-table">
                                <thead>
                                <tr>
                                    <th style={{ width: "45%" }}>
                                        {t("stagePlayDaysList.table.day")}
                                    </th>
                                    <th style={{ width: "35%" }}>
                                        {t("stagePlayDaysList.table.time")}
                                    </th>
                                    <th style={{ width: "20%" }} className="text-right">
                                        {t("stagePlayDaysList.table.actions")}
                                    </th>
                                </tr>
                                </thead>

                                <tbody>
                                {(playDates || []).map((date) => (
                                    <tr key={date.id}>
                                        <td>
                                            <div
                                                style={{
                                                    fontSize: 14,
                                                    fontWeight: 900,
                                                    color: "#14151a",
                                                }}
                                            >
                                                {DAYS[date.day] ||
                                                    t("stagePlayDaysList.days.selectedDay")}
                                            </div>
                                            <div style={ui.muted}>
                                                {t("stagePlayDaysList.table.weeklySlot")}
                                            </div>
                                        </td>

                                        <td>
                                                <span
                                                    style={ui.chip}
                                                    title={t("stagePlayDaysList.table.fromTime")}
                                                >
                                                    <i className="fas fa-clock" />
                                                    {date.fromTime}
                                                </span>
                                        </td>

                                        <td className="text-right">
                                            <button
                                                type="button"
                                                style={{
                                                    ...ui.actionBtn,
                                                    ...ui.actionBtnDanger,
                                                }}
                                                onClick={() => requestDeletePlayDay(date)}
                                                title={t(
                                                    "stagePlayDaysList.table.deleteDayTime"
                                                )}
                                            >
                                                <i className="fas fa-trash" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div style={ui.sectionCard}>
                            <NoData />
                        </div>
                    )}
                </div>
            </div>

            <AddStagePlayDayModal
                modalElem={modalElem}
                stageId={stageId}
                playDates={playDates}
                setPlayDates={setPlayDates}
            />

            <style>{`
                #kbola-playdays .kbola-table thead th {
                  background: rgba(0,0,0,0.02);
                  border-bottom: 1px solid rgba(0,0,0,0.06);
                  color: rgba(20,21,26,0.72);
                  font-size: 12.5px;
                  font-weight: 900;
                  text-transform: uppercase;
                  letter-spacing: 0.02em;
                  padding: 12px 14px;
                }

                #kbola-playdays .kbola-table tbody td {
                  border-top: 1px solid rgba(0,0,0,0.06);
                  padding: 12px 14px;
                  vertical-align: middle;
                }

                #kbola-playdays .kbola-table tbody tr:hover {
                  background: rgba(var(--kbs-primary-rgb, 0,173,255), 0.10);
                }

                #kbola-playdays button:focus {
                  outline: none;
                  box-shadow: 0 0 0 0.2rem rgba(var(--kbs-primary-rgb, 0,173,255), 0.18);
                }
            `}</style>
        </>
    );
}